import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
// https://reactdatepicker.com/#example-exclude-dates
// https://github.com/Hacker0x01/react-datepicker/
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import { ModalOptions } from 'AppSrc/core/components/forms/modal-options';
import { CrossSvg } from 'AppSrc/core/components/common/svg-icons';
import {
  dateToMidnightInLocalTZ,
  dateToMidnightInSpecificTZ,
} from 'AppSrc/core/utility-functions/date-functions';
import { AppConfig } from 'AppSrc/core/config/app-config';
import { DatePickerHeader } from './date-picker-header';
import 'AppSrc/core/style/components/form/form-base-components/sub-components/date-picker-modal.css';

const defaultTz = AppConfig.timezones.default;


const renderDayContents = (day, date) => (
  <div className="calender-day">
    {day}
    <div className="svg-wrapper">
      <CrossSvg />
    </div>
  </div>
);


const KeyItem = ({
  type,
}) => (
  <div className={`key-item key-item-${type}`}>
    {
      type === 'freeze' && (
        <CrossSvg />
      )
    }
  </div>
);

KeyItem.propTypes = {
  type: PropTypes.string,
};

const datePickerKeyArray = [
  {
    text: 'Unavailable date',
    component: <KeyItem type="unavailable" />,
  },
  {
    text: 'Selected date',
    component: <KeyItem type="selected" />,
  },
  {
    text: 'Available date',
    component: <KeyItem type="available" />,
  },
  // to hide freeze from calender section.
  // {
  //   text: 'Freeze date',
  //   component: <KeyItem type="freeze" />,
  // },
];

class DatePickerModal extends PureComponent {
  constructor(props) {
    super(props);
    // savedSelectedDate prop is in the app timezone (london), whereas datepicker works in the local timezone
    const selectedDate = dateToMidnightInLocalTZ(props.savedSelectedDate, defaultTz);
    this.state = {
      selectedDate,
    };
    const selectedEndDate = dateToMidnightInLocalTZ(props.savedSelectedEndDate, defaultTz);
    this.state = {
      selectedEndDate,
    };
    const selectedRaisedDate = dateToMidnightInLocalTZ(props.savedSelectedRaisedDate, defaultTz);
    this.state = {
      selectedRaisedDate,
    };
    this.handleChange = this.handleChange.bind(this);
    this.saveValue = this.saveValue.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  saveValue() {
    const {
      selectedDate,
      selectedEndDate,
      selectedRaisedDate,
    } = this.state;
    // need to convert the date in the local computer timezone back into the app timezone
    const selectedDateInTz = dateToMidnightInSpecificTZ(selectedDate, defaultTz);
    const selectedEndDateInTz = dateToMidnightInSpecificTZ(selectedEndDate, defaultTz);
    const selectedRaisedDateInTz = dateToMidnightInSpecificTZ(selectedRaisedDate, defaultTz);
    // this method also closes the modal

    this.props.updateValue({
      selectedDate: selectedDateInTz,
      selectedEndDate: selectedEndDateInTz,
      selectedRaisedDate: selectedRaisedDateInTz,

    });
  }

  closeModal() {
    this.props.toggleModal();
  }

  handleChange(date) {
    this.setState({
      selectedDate: date,
      selectedEndDate: date,
      selectedRaisedDate: date,
    });
  }

  render() {

    const {
      excludeDates,
      minDate,
      maxDate,
      showKey = true,
    } = this.props;
    const {
      selectedDate,
    } = this.state;
    const modalOptions = {
      apply: this.saveValue,
      cancel: this.closeModal,
    };

    // DatePicker will only work in the local timezone set by the computer
    // and there doesn't seem to be a way to change that.
    // therefore make sure that all dates coming in are changed to the local timezone by brute force
    // if we don't do this, using the datepicker from India will mess up the time calculations and validations
    const localMin = dateToMidnightInLocalTZ(minDate, defaultTz);
    const localMax = dateToMidnightInLocalTZ(maxDate, defaultTz);
    const localExcludeDates = excludeDates ? excludeDates.map((date) => dateToMidnightInLocalTZ(date, defaultTz)) : null;

    return (
      <div className="date-picker-modal">
        <div className="date-picker-modal-shadow" />
        <div className="date-picker-modal-container">
          <div className={`date-picker-modal-inner${showKey ? '' : ' date-picker-modal-inner-no-key'}`}>
            <div className="top">
              <DatePicker
                inline
                shouldCloseOnSelect={false}
                selected={selectedDate}
                onChange={this.handleChange}
                excludeDates={localExcludeDates}
                minDate={localMin}
                maxDate={localMax}
                withPortal
                readOnly
                disabled
                /*
                  We want the calendar to show always show six weeks.
                  However react-datepicker doesn't expose this option.
                  Instead we set peekNextMonth=true to always show one week of the following month.
                  We can then hide a seventh week using css
                 */
                peekNextMonth
                renderDayContents={renderDayContents}
                ref={this.myRef}
                renderCustomHeader={({
                  date,
                  changeYear,
                  decreaseMonth,
                  increaseMonth,
                }) => {
                  const dateMoment = moment(date);
                  return (
                    <DatePickerHeader
                      dateMoment={dateMoment}
                      changeYear={changeYear}
                      decreaseMonth={decreaseMonth}
                      increaseMonth={increaseMonth}
                    />
                  );
                }}
              />
              {showKey && (
                <div className="date-picker-key">
                  {
                    datePickerKeyArray.map((key) => (
                      <div
                        key={key.text}
                        className="key-item-wrapper"
                      >
                        {key.component}
                        <p>{key.text}</p>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
            <div className="bottom">
              <ModalOptions
                modalOptions={modalOptions}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

DatePickerModal.propTypes = {
  savedSelectedDate: PropTypes.instanceOf(Date),
  savedSelectedEndDate: PropTypes.instanceOf(Date),
  savedSelectedRaisedDate: PropTypes.instanceOf(Date),
  updateValue: PropTypes.func,
  toggleModal: PropTypes.func,
  excludeDates: PropTypes.arrayOf(PropTypes.number),
  minDate: PropTypes.number,
  maxDate: PropTypes.number,
  showKey: PropTypes.bool,
};


export {
  DatePickerModal,
};
